import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, ShoppingBag, MessageCircle, X, Check, Star, Heart } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useChat } from "../../context/ChatContext";
import { useTheme } from "../../context/ThemeContext";
import { useWishlist } from "../../context/WishlistContext";
import { useNavigate } from "react-router-dom";
import { isPlaceholderPrice, priceLabel } from "../../utils/price";
import { stockForVariant, priceForSize } from "../../utils/variant";
import { recommendedSize, compareAtForSize, recommendedLabel } from "../../utils/formatMetrics";
import { getTierNames } from "../../utils/tierNames";

const tierGradient = {
  start: "from-[#0F1A2E] via-[#0A1426] to-[#060A14]",
  power: "from-[#152038] via-[#0E172B] to-[#060A14]",
  elite: "from-[#1A2540] via-[#101A30] to-[#060A14]",
};

// Stable hash to vary fallback tile per product
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const initials = (name) =>
  name
    .replace(/[®™]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const tierStyles = {
  start: {
    card: "bg-[var(--lv-surface,#0A1121)] border-[#1E293B] hover:border-[#C0C0C0]/40",
    badge: "bg-white/5 text-[#94A3B8] border border-white/10",
    cta:
      "bg-[#E5E4E2] text-[#060A14] hover:bg-white",
    glow: "",
  },
  power: {
    card:
      "bg-[var(--lv-surface,#0A1121)] border-[#1E293B] hover:border-[#C0C0C0]/50 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(192,192,192,0.25)]",
    badge: "bg-white/5 text-[#C0C0C0] border border-[#C0C0C0]/20",
    cta:
      "bg-[#E5E4E2] text-[#060A14] hover:bg-white",
    glow: "",
  },
  elite: {
    card:
      "bg-[var(--lv-surface,#0A1121)] border-[#C0C0C0]/30 hover:border-[#C0C0C0]/70 shadow-[inset_0_1px_0_rgba(192,192,192,0.15)]",
    badge:
      "bg-[#C0C0C0]/10 text-[#E5E4E2] border border-[#C0C0C0]/40",
    cta:
      "bg-[#E5E4E2] text-[#060A14] hover:bg-white",
    glow: "before:content-[''] before:absolute before:inset-0 before:rounded-[6px] before:bg-gradient-to-br before:from-[#C0C0C0]/20 before:via-transparent before:to-[#C0C0C0]/10 before:opacity-0 hover:before:opacity-100 before:transition-opacity",
  },
};

const ProductCard = ({ product, index, onOpen }) => {
  const s = tierStyles[product.tier];
  const [imgError, setImgError] = useState(false);
  const cart = useCart();
  const chat = useChat();
  const { settings } = useTheme();
  const tierNames = getTierNames(settings);
  const { isWishlisted, toggle: toggleWishlist } = useWishlist();
  const navigate = useNavigate();

  const flavors = product.flavors || [];
  const sizes = product.sizes || [];
  const hasVariants = flavors.length > 0 || sizes.length > 0;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedFlavor, setPickedFlavor] = useState(flavors[0] || "");
  const [pickedSize, setPickedSize] = useState(sizes[0] || "");

  const isExternalPhoto =
    product.image && (product.image.startsWith("http") || product.image.startsWith("/api/uploads"));

  const handleOpen = () => {
    if (onOpen) onOpen(product);
  };
  const NavTrigger = product.slug ? Link : "button";
  const navProps = product.slug ? { to: `/prodotto/${product.slug}` } : { type: "button", onClick: handleOpen };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (hasVariants) {
      setPickedFlavor(flavors[0] || "");
      setPickedSize(sizes[0] || "");
      setPickerOpen(true);
      return;
    }
    cart.addItem(product, 1);
  };

  const confirmVariant = (e) => {
    e.stopPropagation();
    cart.addItem(product, 1, { flavor: pickedFlavor, size: pickedSize });
    setPickerOpen(false);
  };

  const handleAskRenato = (e) => {
    e.stopPropagation();
    chat.openWith(`Mi interessa *${product.name}* (${product.brand}). Puoi darmi maggiori informazioni e il prezzo?`);
  };

  const handleToggleWishlist = async (e) => {
    e.stopPropagation();
    const res = await toggleWishlist(product.id);
    if (res.needsLogin) navigate("/login");
  };

  const isOutOfStock = Number(product.stock || 0) <= 0;
  const variantStock = product.variant_stock || {};
  const pickedComboStock = stockForVariant(variantStock, pickedFlavor, pickedSize);
  const pickedComboOut = pickedComboStock !== undefined && pickedComboStock <= 0;
  const displayPrice = priceForSize(product, pickedSize);
  const compareAt = compareAtForSize(product, pickedSize);
  const recSize = settings?.layout?.show_recommended !== false ? recommendedSize(product) : "";

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (index % 12) * 0.04 }}
      data-testid={`product-card-${product.id}`}
      className={`lv-product-card group relative flex flex-col rounded-[6px] border ${s.card} ${s.glow} transition-all duration-300 overflow-hidden`}
    >
      {product.tier === "elite" && (
        <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#060A14]/80 backdrop-blur-md border border-[#C0C0C0]/40">
          <Award className="w-3 h-3 text-[#C0C0C0]" />
          <span className="text-[9px] tracking-[0.22em] uppercase text-[#E5E4E2]">
            {tierNames.elite.name}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={handleToggleWishlist}
        data-testid={`wishlist-toggle-${product.id}`}
        title={isWishlisted(product.id) ? "Rimuovi dai preferiti" : "Salva nei preferiti"}
        className={`absolute top-3 left-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full backdrop-blur-md border transition-colors ${
          isWishlisted(product.id)
            ? "bg-red-500/20 border-red-400/50 text-red-400"
            : "bg-[#060A14]/70 border-white/20 text-[#E5E4E2] hover:text-white hover:border-white/40"
        }`}
      >
        <Heart className="w-3.5 h-3.5" fill={isWishlisted(product.id) ? "currentColor" : "none"} />
      </button>

      {/* Image area (click → /prodotto/:slug, modal only if no slug) */}
      <NavTrigger
        {...navProps}
        data-testid={`product-image-trigger-${product.id}`}
        className={`relative block aspect-[4/5] overflow-hidden text-left ${
          isExternalPhoto
            ? "bg-gradient-to-b from-[#F4F5F7] to-[#D9DDE3]"
            : "bg-[#060A14]"
        }`}
      >
        {!imgError ? (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            onError={() => setImgError(true)}
            data-testid={`product-image-${product.id}`}
            className={`absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105 ${
              isExternalPhoto ? "object-contain p-6" : "object-cover"
            }`}
          />
        ) : (
          <div
            data-testid={`product-fallback-${product.id}`}
            className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${tierGradient[product.tier]} overflow-hidden`}
          >
            {/* decorative concentric arcs unique per product */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                background: `radial-gradient(circle at ${30 + (hash(product.id) % 40)}% ${20 + (hash(product.name) % 60)}%, rgba(192,192,192,0.45), transparent 55%)`,
              }}
            />
            <div className="absolute inset-x-0 top-6 flex justify-center">
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#94A3B8]">
                {product.brand}
              </span>
            </div>
            <div className="relative z-10 flex flex-col items-center gap-3">
              <div className="w-20 h-20 rounded-full border border-[#C0C0C0]/40 flex items-center justify-center bg-[#060A14]/40 backdrop-blur-sm">
                <span className="font-[Outfit] text-[#E5E4E2] text-2xl tracking-[0.18em]">
                  {initials(product.name)}
                </span>
              </div>
              <span className="font-[Outfit] text-white text-base tracking-tight max-w-[80%] text-center leading-tight px-2">
                {product.name}
              </span>
            </div>
            <div className="absolute bottom-20 inset-x-0 flex justify-center">
              <span className="h-px w-16 bg-[#C0C0C0]/40" />
            </div>
          </div>
        )}

        {/* Bottom info row */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pt-12 pb-4 flex items-end justify-between bg-gradient-to-t from-[#060A14]/95 via-[#060A14]/60 to-transparent">
          <span
            className={`px-2.5 py-1 rounded-full text-[9px] tracking-[0.24em] uppercase ${s.badge}`}
            data-testid={`product-brand-${product.id}`}
          >
            {product.brand}
          </span>
          {settings?.layout?.hide_prices ? (
            <span
              className="px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.24em] uppercase bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444]"
              data-testid={`product-terminated-${product.id}`}
            >
              Prodotto terminato
            </span>
          ) : isPlaceholderPrice(product.price) || product.custom_price_label ? (
            <span
              className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.24em] uppercase bg-[#060A14]/70 border border-[#C0C0C0]/30 text-[#E5E4E2]"
              data-testid={`product-price-on-request-${product.id}`}
            >
              {priceLabel(product, settings)}
            </span>
          ) : (
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-[0.10em] bg-[#C0C0C0]/10 border border-[#C0C0C0]/40 text-white inline-flex items-center gap-1.5"
              data-testid={`product-price-${product.id}`}
            >
              {compareAt > displayPrice && (
                <span className="text-[#94A3B8] font-normal line-through" data-testid={`product-compare-at-${product.id}`}>€{Number(compareAt).toFixed(2)}</span>
              )}
              €{Number(displayPrice).toFixed(2)}
            </span>
          )}
        </div>
      </NavTrigger>

      {/* Body */}
      <div className="relative flex flex-col flex-1 p-5 sm:p-6 gap-4">
        <NavTrigger
          {...navProps}
          className="block text-left"
          data-testid={`product-name-trigger-${product.id}`}
        >
          <h3
            className="font-[Outfit] text-white text-lg sm:text-xl tracking-tight leading-tight hover:text-[#C0C0C0] transition-colors"
            data-testid={`product-name-${product.id}`}
          >
            {product.name}
          </h3>
        </NavTrigger>
        <p className="text-[13px] text-[#94A3B8] leading-relaxed font-[Manrope] line-clamp-3">
          {product.desc}
        </p>

        {Number(product.rating_count || 0) > 0 && (
          <div className="flex items-center gap-1.5" data-testid={`product-rating-${product.id}`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-3 h-3 ${n <= Math.round(product.rating_avg) ? "text-amber-300 fill-amber-300" : "text-white/15"}`} />
            ))}
            <span className="text-[10px] text-[#94A3B8]">{product.rating_avg} ({product.rating_count})</span>
          </div>
        )}

        {hasVariants && (
          <div className="space-y-1.5" data-testid={`product-variants-info-${product.id}`}>
            {flavors.length > 0 && (
              <p className="text-[10px] text-[#94A3B8] tracking-[0.16em] uppercase">
                <span className="text-[#E5E4E2]">Gusti:</span> {flavors.join(" · ")}
              </p>
            )}
            {sizes.length > 0 && (
              <p className="text-[10px] text-[#94A3B8] tracking-[0.16em] uppercase">
                <span className="text-[#E5E4E2]">Formati:</span> {sizes.join(" · ")}
              </p>
            )}
          </div>
        )}

        <div className="mt-auto flex flex-col gap-2 relative">
          {pickerOpen && (
            <div data-testid={`variant-picker-${product.id}`}
                 className="absolute left-0 right-0 bottom-0 z-30 max-h-[70vh] overflow-y-auto bg-[#0A1121]/98 backdrop-blur-md rounded-md border border-[#C0C0C0]/40 p-3 flex flex-col gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
              <div className="flex items-center justify-between">
                <span className="text-[9px] tracking-[0.28em] uppercase text-[#C0C0C0]">Scegli variante</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); setPickerOpen(false); }}
                        data-testid={`variant-cancel-${product.id}`}
                        className="text-[#94A3B8] hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {flavors.length > 0 && (
                <div>
                  <p className="text-[9px] text-[#94A3B8] tracking-[0.18em] uppercase mb-1">Gusto</p>
                  <div className="flex flex-wrap gap-1">
                    {flavors.map((f) => {
                      const stock = stockForVariant(variantStock, f, pickedSize);
                      const esaurito = stock !== undefined && stock <= 0;
                      return (
                      <button key={f} type="button" onClick={(e) => { e.stopPropagation(); if (!esaurito) setPickedFlavor(f); }}
                              disabled={esaurito}
                              data-testid={`variant-flavor-${product.id}-${f}`}
                              className={`px-2 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase border transition-colors ${
                                pickedFlavor === f ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white"
                              } ${esaurito ? "opacity-40 line-through cursor-not-allowed" : ""}`}>{f}{esaurito ? " (Esaurito)" : ""}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              {sizes.length > 0 && (
                <div>
                  <p className="text-[9px] text-[#94A3B8] tracking-[0.18em] uppercase mb-1">Formato</p>
                  <div className="flex flex-wrap gap-1">
                    {sizes.map((sz) => {
                      const stock = stockForVariant(variantStock, pickedFlavor, sz);
                      const esaurito = stock !== undefined && stock <= 0;
                      const isRec = sz === recSize;
                      return (
                      <button key={sz} type="button" onClick={(e) => { e.stopPropagation(); if (!esaurito) setPickedSize(sz); }}
                              disabled={esaurito}
                              data-testid={`variant-size-${product.id}-${sz}`}
                              className={`px-2 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase border transition-colors ${
                                pickedSize === sz ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : isRec ? "border-emerald-400/60 text-emerald-200" : "border-white/15 text-[#94A3B8] hover:text-white"
                              } ${esaurito ? "opacity-40 line-through cursor-not-allowed" : ""}`}>{sz}{esaurito ? " (Esaurito)" : ""}{isRec ? " ★" : ""}</button>
                      );
                    })}
                  </div>
                  {recSize && (
                    <p className="mt-1 text-[9px] text-emerald-300/90 tracking-[0.16em] uppercase" data-testid={`card-recommended-note-${product.id}`}>
                      ★ {recommendedLabel(product, settings)}: {recSize}
                    </p>
                  )}
                </div>
              )}
              <button type="button" onClick={confirmVariant} disabled={pickedComboOut} data-testid={`variant-confirm-${product.id}`}
                      className="mt-1 inline-flex items-center justify-center gap-1.5 bg-[#C0C0C0] text-[#060A14] py-2 rounded-sm text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                <Check className="w-3 h-3" /> {pickedComboOut ? "Esaurito" : "Aggiungi"}
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            data-testid={`add-to-cart-${product.id}`}
            className={`inline-flex items-center justify-between gap-3 px-5 py-3 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium transition-all duration-300 ${s.cta} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <span className="inline-flex items-center gap-2">
              <ShoppingBag className="w-3.5 h-3.5" />
              {isOutOfStock ? "Esaurito" : "Aggiungi al carrello"}
            </span>
            {!isOutOfStock && <ArrowUpRight className="w-4 h-4" />}
          </button>
          <div className="grid grid-cols-2 gap-2">
            {product.slug ? (
              <Link
                to={`/prodotto/${product.slug}`}
                data-testid={`product-details-btn-${product.id}`}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.22em] uppercase text-[#94A3B8] border border-white/10 hover:text-white hover:border-[#C0C0C0]/40 transition-colors"
              >
                Dettagli
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleOpen}
                data-testid={`product-details-btn-${product.id}`}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.22em] uppercase text-[#94A3B8] border border-white/10 hover:text-white hover:border-[#C0C0C0]/40 transition-colors"
              >
                Dettagli
              </button>
            )}
            <button
              type="button"
              onClick={handleAskRenato}
              data-testid={`ask-renato-${product.id}`}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.22em] uppercase text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/10 transition-colors"
            >
              <MessageCircle className="w-3 h-3" /> Leo
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default ProductCard;
